import { useCallback } from 'react'

import { toast } from 'sonner'

/**
 * useShare - Hook for sharing the current page URL
 *
 * Uses the Web Share API if available, falls back to clipboard copy.
 * Strips query parameters from the URL before sharing.
 *
 * @example
 * ```tsx
 * const handleShare = useShare()
 * <Button onClick={handleShare}>Share</Button>
 * ```
 */
export function useShare() {
  return useCallback(async () => {
    const url = new URL(window.location.href)
    url.search = '' // Remove query params
    const shareUrl = url.toString()

    if (
      navigator.canShare &&
      navigator.canShare({ title: document.title, url: shareUrl })
    ) {
      try {
        await navigator.share({
          title: document.title,
          url: shareUrl,
        })
        toast.success('Shared successfully!')
      } catch {
        // User cancelled or share failed silently
      }
    } else if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl)
        toast.success('Link copied to clipboard!')
      } catch {
        toast.error('Failed to copy link')
      }
    } else {
      toast.error('Sharing is not supported in this browser')
    }
  }, [])
}
